import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Typography, Paper, Checkbox, FormControlLabel, Button, List, ListItem, ListItemText, Divider, Chip, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import { networkDataService } from '../services/networkData';

const IntersectionView = ({
  circles = [],
  onCircleClick,
  onEditClick,
  onAddMemberClick,
  selectedCircle,
  hoveredCircle,
  setHoveredCircle
}) => {
  const svgRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [touchStartPoint, setTouchStartPoint] = useState(null);
  const [visibleCircles, setVisibleCircles] = useState({});

  const commonMembers = networkDataService.getAllCommonMembers();
  const center = { x: 1000, y: 500 };
  const baseRadius = 200;
  const overlapRadius = 100;

  // Initialize visible circles state
  useEffect(() => {
    const initialVisibility = {};
    circles.forEach(circle => {
      initialVisibility[circle.id] = true;
    });
    setVisibleCircles(initialVisibility);
  }, [circles]);

  // Calculate circle positions for Venn diagram
  const getCirclePositions = () => {
    const positions = {};
    const visibleCircleIds = Object.entries(visibleCircles)
      .filter(([_, isVisible]) => isVisible)
      .map(([id]) => id);
    
    const visibleCirclesCount = visibleCircleIds.length;
    const angleStep = (2 * Math.PI) / visibleCirclesCount;

    visibleCircleIds.forEach((circleId, index) => {
      const angle = index * angleStep;
      positions[circleId] = {
        x: center.x + Math.cos(angle) * baseRadius,
        y: center.y + Math.sin(angle) * baseRadius
      };
    });

    return positions;
  };

  const circlePositions = getCirclePositions();

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    let isDragging = false;
    let startPoint = { x: 0, y: 0 };

    const handleMouseDown = (e) => {
      isDragging = true;
      startPoint = { x: e.clientX, y: e.clientY };
      svg.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startPoint.x;
      const dy = e.clientY - startPoint.y;
      setTransform(prev => ({
        ...prev,
        x: prev.x + dx,
        y: prev.y + dy
      }));
      startPoint = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging = false;
      svg.style.cursor = 'grab';
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        isDragging = true;
        startPoint = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        e.preventDefault();
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - startPoint.x;
      const dy = e.touches[0].clientY - startPoint.y;
      setTransform(prev => ({
        ...prev,
        x: prev.x + dx,
        y: prev.y + dy
      }));
      startPoint = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      e.preventDefault();
    };

    const handleTouchEnd = () => {
      isDragging = false;
    };

    const handleWheel = (e) => {
      const isZoomGesture = e.ctrlKey || e.metaKey || 
        (e.deltaMode === 0 && Math.abs(e.deltaY) > 50);
      
      if (isZoomGesture) {
        e.preventDefault();
        const zoomFactor = Math.pow(0.95, Math.sign(e.deltaY));
        setTransform(prev => ({
          ...prev,
          scale: Math.min(Math.max(prev.scale * zoomFactor, 0.5), 2)
        }));
      }
    };

    svg.addEventListener('mousedown', handleMouseDown);
    svg.addEventListener('mousemove', handleMouseMove);
    svg.addEventListener('mouseup', handleMouseUp);
    svg.addEventListener('mouseleave', handleMouseUp);
    svg.addEventListener('touchstart', handleTouchStart, { passive: false });
    svg.addEventListener('touchmove', handleTouchMove, { passive: false });
    svg.addEventListener('touchend', handleTouchEnd);
    svg.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      svg.removeEventListener('mousedown', handleMouseDown);
      svg.removeEventListener('mousemove', handleMouseMove);
      svg.removeEventListener('mouseup', handleMouseUp);
      svg.removeEventListener('mouseleave', handleMouseUp);
      svg.removeEventListener('touchstart', handleTouchStart);
      svg.removeEventListener('touchmove', handleTouchMove);
      svg.removeEventListener('touchend', handleTouchEnd);
      svg.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Calculate intersection areas
  const getIntersectionAreas = () => {
    const areas = [];
    const positions = getCirclePositions();
    const visibleCircleIds = Object.entries(visibleCircles)
      .filter(([_, isVisible]) => isVisible)
      .map(([id]) => id);

    // Calculate intersections between all pairs of visible circles
    for (let i = 0; i < visibleCircleIds.length; i++) {
      for (let j = i + 1; j < visibleCircleIds.length; j++) {
        const circle1 = circles.find(c => c.id === visibleCircleIds[i]);
        const circle2 = circles.find(c => c.id === visibleCircleIds[j]);
        const common = commonMembers[`${circle1.id}-${circle2.id}`];
        
        if (common && common.length > 0) {
          const pos1 = positions[circle1.id];
          const pos2 = positions[circle2.id];
          
          // Calculate intersection point
          const dx = pos2.x - pos1.x;
          const dy = pos2.y - pos1.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < baseRadius * 2) {
            const midX = (pos1.x + pos2.x) / 2;
            const midY = (pos1.y + pos2.y) / 2;
            
            areas.push({
              x: midX,
              y: midY,
              count: common.length
            });
          }
        }
      }
    }

    return areas;
  };

  const intersectionAreas = getIntersectionAreas();

  const handleCircleToggle = (circleId) => {
    setVisibleCircles(prev => ({
      ...prev,
      [circleId]: !prev[circleId]
    }));
  };

  const handleSelectAll = () => {
    const allVisible = {};
    circles.forEach(circle => {
      allVisible[circle.id] = true;
    });
    setVisibleCircles(allVisible);
  };

  const handleDeselectAll = () => {
    const allHidden = {};
    circles.forEach(circle => {
      allHidden[circle.id] = false;
    });
    setVisibleCircles(allHidden);
  };

  // Get common members across all selected circles
  const getCommonMembersAcrossSelectedCircles = () => {
    const selectedCircleIds = Object.entries(visibleCircles)
      .filter(([_, isVisible]) => isVisible)
      .map(([id]) => id);

    if (selectedCircleIds.length < 2) return [];

    // Get all members from the first selected circle
    const firstCircle = circles.find(c => c.id === selectedCircleIds[0]);
    if (!firstCircle) return [];

    const firstCircleMembers = networkDataService.getCircleMembers(firstCircle.id);
    
    // Filter members that exist in all other selected circles
    return firstCircleMembers.filter(member => {
      return selectedCircleIds.slice(1).every(circleId => {
        const circle = circles.find(c => c.id === circleId);
        if (!circle) return false;
        const circleMembers = networkDataService.getCircleMembers(circle.id);
        return circleMembers.some(cm => cm.id === member.id);
      });
    });
  };

  const commonMembersList = getCommonMembersAcrossSelectedCircles();

  return (
    <Box sx={{
      width: '100%',
      bgcolor: 'background.paper',
      borderRadius: 2,
      overflow: 'hidden',
      position: 'relative',
      touchAction: 'none',
      mb: 4,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden' }}>
        <Box
          ref={svgRef}
          sx={{
            width: { xs: '100%', md: '70%' },
            height: { xs: 320, md: 500 },
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            },
            touchAction: 'none',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <svg 
            width="100%" 
            height="100%" 
            viewBox="0 0 2000 1000"
            preserveAspectRatio="xMidYMid meet"
            style={{
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              transformOrigin: 'center'
            }}
          >
            <g transform="translate(0, 0)">
              {/* Render circles */}
              {circles.map((circle) => {
                const pos = circlePositions[circle.id];
                if (!pos || !visibleCircles[circle.id]) return null;

                const isHovered = hoveredCircle === circle.id;
                const isSelected = selectedCircle === circle.id;

                return (
                  <g 
                    key={circle.id} 
                    transform={`translate(${pos.x},${pos.y})`}
                    onMouseEnter={() => setHoveredCircle(circle.id)}
                    onMouseLeave={() => setHoveredCircle(null)}
                    onClick={() => onCircleClick(circle)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHoveredCircle(circle.id);
                      setTouchStartPoint({ x: e.touches[0].clientX, y: e.touches[0].clientY });
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (touchStartPoint) {
                        const dx = e.touches[0].clientX - touchStartPoint.x;
                        const dy = e.touches[0].clientY - touchStartPoint.y;
                        setTransform(prev => ({
                          ...prev,
                          x: prev.x + dx,
                          y: prev.y + dy
                        }));
                        setTouchStartPoint({ x: e.touches[0].clientX, y: e.touches[0].clientY });
                      }
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onCircleClick(circle);
                      setHoveredCircle(null);
                      setTouchStartPoint(null);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle
                      r={baseRadius}
                      fill={circle.color}
                      opacity={isHovered || isSelected ? 0.4 : 0.2}
                      stroke={circle.color}
                      strokeWidth={isHovered || isSelected ? 6 : 4}
                    />
                    <text
                      x={0}
                      y={baseRadius + 20}
                      textAnchor="middle"
                      fontSize="24"
                      fill={isHovered || isSelected ? "#000" : "#333"}
                      fontWeight="bold"
                    >
                      {circle.name}
                    </text>
                    <text
                      x={0}
                      y={baseRadius + 45}
                      textAnchor="middle"
                      fontSize="18"
                      fill={isHovered || isSelected ? "#444" : "#666"}
                    >
                      {circle.members} members
                    </text>
                    {(isHovered || isSelected) && (
                      <g transform={`translate(${baseRadius + 20}, ${-baseRadius - 20})`}>
                        <foreignObject width="24" height="24">
                          <IconButton
                            size="small"
                            onClick={(e) => onEditClick(e, circle)}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              onEditClick(e, circle);
                            }}
                            sx={{ 
                              bgcolor: 'background.paper',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </foreignObject>
                      </g>
                    )}
                  </g>
                );
              })}

              {/* Render intersection areas */}
              {intersectionAreas.map((area, i) => (
                <g key={`intersection-${i}`} transform={`translate(${area.x},${area.y})`}>
                  <circle
                    r={25}
                    fill="white"
                    stroke="#666"
                    strokeWidth="2"
                  />
                  <text
                    x={0}
                    y={8}
                    textAnchor="middle"
                    fontSize="20"
                    fill="#000"
                  >
                    {area.count}
                  </text>
                </g>
              ))}
            </g>
          </svg>
        </Box>

        {/* Legend Box */}
        <Paper
          sx={{
            width: { xs: '100%', md: '30%' },
            maxHeight: { xs: 220, md: 500 },
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflow: 'auto',
            borderTop: { xs: '1px solid', md: 'none' },
            borderLeft: { xs: 'none', md: '1px solid' },
            borderColor: 'divider',
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Circle Visibility
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleSelectAll}>All</Button>
              <Button size="small" onClick={handleDeselectAll}>None</Button>
            </Box>
          </Box>
          {circles.map((circle) => (
            <FormControlLabel
              key={circle.id}
              control={
                <Checkbox
                  checked={visibleCircles[circle.id] || false}
                  onChange={() => handleCircleToggle(circle.id)}
                  sx={{
                    color: circle.color,
                    '&.Mui-checked': {
                      color: circle.color,
                    },
                    padding: '4px'
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: circle.color }}>
                  {circle.name}
                </Typography>
              }
              sx={{ margin: 0 }}
            />
          ))}
        </Paper>
      </Box>

      {/* Common Members Pane */}
      {commonMembersList.length > 0 && (
        <Paper 
          sx={{ 
            mt: 2,
            p: 2,
            maxHeight: '200px',
            overflow: 'auto'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Common Members ({commonMembersList.length})
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1,
            '& .MuiChip-root': {
              height: '24px',
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.75rem'
              }
            }
          }}>
            {commonMembersList.map((member) => (
              <Tooltip
                key={member.id}
                title={member.name}
                arrow
                placement="top"
              >
                <Chip
                  label={member.name}
                  size="small"
                  sx={{ 
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    maxWidth: '200px',
                    '& .MuiChip-label': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default IntersectionView; 